import { useState } from 'react'
import { useOptimizer } from '@/hooks/useOptimizer'
import { optimizeEPO, type EPOResult } from '@/lib/api'
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
} from 'recharts'
import {
  Sliders, Play, TrendingUp, BarChart2, ArrowRightLeft,
  Loader2, AlertTriangle, Target, Shield, Zap,
  type LucideIcon,
} from 'lucide-react'
import { clsx } from 'clsx'
import PageHeader from '@/components/ui/PageHeader'

// ── Helpers ──────────────────────────────────────────────────────────────────

function pct(n: number) { return `${n >= 0 ? '+' : ''}${n.toFixed(2)}%` }

const COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1',
  '#84CC16', '#D946EF', '#0EA5E9', '#FB923C', '#A855F7',
]

function KpiCard({ label, value, sub, positive, icon: Icon, hero }: {
  label: string; value: string; sub?: string; positive?: boolean
  icon?: LucideIcon; hero?: boolean
}) {
  return (
    <div className="stat-card">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon size={14} className="text-text-muted" />}
        <p className="stat-label">{label}</p>
      </div>
      <p className={clsx(hero ? 'stat-hero' : 'stat-value text-xl', positive === true ? 'text-emerald' : positive === false ? 'text-rose-light' : '')}>
        {value}
      </p>
      {sub && <p className="text-xs text-text-muted mt-0.5">{sub}</p>}
    </div>
  )
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function OptimizerPage() {
  const {
    markowitz, riskParity, frontier, rebalance, comparison,
    strategy, tickers, loading, error,
    setStrategy, setTickers, runOptimize, fetchComparison, clear,
  } = useOptimizer()

  const [tickerInput, setTickerInput] = useState(tickers.join(', '))

  // EPO panel state — independent of the legacy useOptimizer hook
  const [epoShrinkage, setEpoShrinkage] = useState(0.5)
  const [epoResult, setEpoResult] = useState<EPOResult | null>(null)
  const [epoLoading, setEpoLoading] = useState(false)
  const [epoError, setEpoError] = useState<string | null>(null)
  const handleRunEPO = async () => {
    const parsed = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
    if (parsed.length < 2) { setEpoError('Need at least 2 tickers'); return }
    setEpoLoading(true)
    setEpoError(null)
    try {
      const res = await optimizeEPO(parsed, epoShrinkage)
      if (res.error) {
        setEpoError(res.error)
        setEpoResult(null)
      } else {
        setEpoResult(res)
      }
    } catch (e: any) {
      setEpoError(e?.response?.data?.detail || e?.message || 'EPO solve failed')
    } finally {
      setEpoLoading(false)
    }
  }

  const handleOptimize = () => {
    const parsed = tickerInput.split(',').map(t => t.trim().toUpperCase()).filter(Boolean)
    if (parsed.length < 2) return
    setTickers(parsed)
    runOptimize(parsed)
  }

  // Get the active portfolio based on selected strategy
  const activePortfolio = strategy === 'risk_parity'
    ? riskParity
    : strategy === 'min_variance'
      ? markowitz?.min_variance
      : markowitz?.max_sharpe

  const weights = activePortfolio?.weights ?? {}
  const pieData = Object.entries(weights)
    .filter(([, w]) => w > 0.001)
    .map(([ticker, w]) => ({ name: ticker, value: Math.round(w * 10000) / 100 }))
    .sort((a, b) => b.value - a.value)

  const frontierData = frontier?.frontier?.map(p => ({
    x: p.volatility_pct,
    y: p.return_pct,
    sharpe: p.sharpe_ratio,
  })) ?? []

  // Current portfolio marker for frontier chart
  const currentMarker = activePortfolio
    ? [{ x: activePortfolio.volatility, y: activePortfolio.expected_return, sharpe: activePortfolio.sharpe_ratio }]
    : []

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <PageHeader
        title="Portfolio Optimizer"
        icon={Sliders}
        subtitle="Modern Portfolio Theory -- Efficient Frontier, Optimal Weights, Rebalancing"
        actions={
          <button
            onClick={() => fetchComparison()}
            disabled={loading}
            className="btn-secondary text-sm gap-2"
          >
            <ArrowRightLeft size={14} />
            Current vs Optimal
          </button>
        }
      />

      {/* Input Controls */}
      <div className="glass rounded-xl p-5 space-y-4">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[300px]">
            <label className="block text-xs text-text-muted mb-1">Tickers (comma-separated)</label>
            <input
              type="text"
              value={tickerInput}
              onChange={e => setTickerInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleOptimize()}
              placeholder="AAPL, MSFT, NVDA, GOOGL, AMZN"
              className="input px-3 py-2"
            />
          </div>

          {/* Strategy selector */}
          <div>
            <label className="block text-xs text-text-muted mb-1">Strategy</label>
            <div className="flex rounded-lg border border-border overflow-hidden">
              {[
                { key: 'max_sharpe' as const, label: 'Max Sharpe', icon: TrendingUp },
                { key: 'min_variance' as const, label: 'Min Variance', icon: Shield },
                { key: 'risk_parity' as const, label: 'Risk Parity', icon: Target },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setStrategy(key)}
                  className={clsx(
                    'flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors',
                    strategy === key
                      ? 'bg-blue/15 text-blue-light'
                      : 'bg-surface-card text-text-secondary hover:bg-surface-hover',
                  )}
                >
                  <Icon size={12} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <button
            onClick={handleOptimize}
            disabled={loading || tickerInput.split(',').filter(Boolean).length < 2}
            className="btn-primary px-6 py-2.5 text-sm gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            Optimize
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="card p-4 border-rose/30 bg-rose/5">
          <p className="text-sm text-rose-light flex items-center gap-2">
            <AlertTriangle size={14} /> {error}
          </p>
        </div>
      )}

      {/* Results */}
      {activePortfolio && !loading && (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 stagger-children">
            <KpiCard
              label="Expected Return"
              value={pct(activePortfolio.expected_return)}
              positive={activePortfolio.expected_return > 0}
              icon={TrendingUp}
            />
            <KpiCard
              label="Volatility"
              value={`${(activePortfolio.volatility ?? 0).toFixed(2)}%`}
              icon={BarChart2}
            />
            <KpiCard
              label="Sharpe Ratio"
              value={(activePortfolio.sharpe_ratio ?? 0).toFixed(3)}
              positive={activePortfolio.sharpe_ratio > 1}
              icon={Target}
              hero
            />
            <KpiCard
              label="Assets"
              value={String(Object.keys(weights).filter(k => weights[k] > 0.001).length)}
              sub={`of ${tickers.length} tickers`}
              icon={Sliders}
            />
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Pie Chart */}
            <div className="card p-5">
              <h2 className="panel-title mb-4">
                Optimal Weights
                <span className="text-text-muted font-normal ml-2">
                  ({strategy === 'max_sharpe' ? 'Max Sharpe' : strategy === 'min_variance' ? 'Min Variance' : 'Risk Parity'})
                </span>
              </h2>
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    outerRadius={120}
                    label={({ name, value }) => `${name} ${value.toFixed(1)}%`}
                  >
                    {pieData.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid #2a3040', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                    formatter={(value: number) => [`${value.toFixed(1)}%`, 'Weight']}
                  />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            {/* Efficient Frontier */}
            <div className="card p-5">
              <h2 className="panel-title mb-4">Efficient Frontier</h2>
              {frontierData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <ScatterChart margin={{ top: 10, right: 20, bottom: 20, left: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a3040" />
                    <XAxis
                      dataKey="x" type="number" name="Volatility"
                      label={{ value: 'Volatility (%)', position: 'bottom', fill: '#6b7280', fontSize: 11 }}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                    />
                    <YAxis
                      dataKey="y" type="number" name="Return"
                      label={{ value: 'Return (%)', angle: -90, position: 'insideLeft', fill: '#6b7280', fontSize: 11 }}
                      tick={{ fill: '#6b7280', fontSize: 10 }}
                    />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1f2e', border: '1px solid #2a3040', borderRadius: '8px', color: 'rgb(var(--text-primary))'}}
                      formatter={(value: number, name: string) => [
                        `${value.toFixed(2)}%`,
                        name === 'x' ? 'Volatility' : 'Return',
                      ]}
                    />
                    <Scatter name="Frontier" data={frontierData} fill="#3B82F6" />
                    {currentMarker.length > 0 && (
                      <Scatter name="Selected Portfolio" data={currentMarker} fill="#10B981" shape="diamond" />
                    )}
                  </ScatterChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-80 flex items-center justify-center text-text-muted text-sm">
                  Run optimization to see frontier
                </div>
              )}
            </div>
          </div>

          {/* Weights Table + Individual Stats */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Weights Table */}
            <div className="card p-5">
              <h2 className="panel-title mb-4">Portfolio Weights</h2>
              <div className="table-responsive overflow-x-auto">
                <table className="table-pro w-full text-sm">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th className="!text-right">Weight</th>
                      <th className="w-1/2">Allocation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(weights)
                      .sort(([, a], [, b]) => b - a)
                      .map(([ticker, w], i) => (
                        <tr key={ticker}>
                          <td className="font-medium text-text-primary">{ticker}</td>
                          <td className="text-right">{(w * 100).toFixed(1)}%</td>
                          <td>
                            <div className="w-full bg-surface-hover rounded-full h-2">
                              <div
                                className="h-2 rounded-full"
                                style={{
                                  width: `${Math.min(w * 100, 100)}%`,
                                  backgroundColor: COLORS[i % COLORS.length],
                                }}
                              />
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Risk/Return Comparison */}
            <div className="card p-5">
              <h2 className="panel-title mb-4">Risk / Return Comparison</h2>
              <div className="table-responsive overflow-x-auto">
                <table className="table-pro w-full text-sm">
                  <thead>
                    <tr>
                      <th>Ticker</th>
                      <th className="!text-right">Exp. Return</th>
                      <th className="!text-right">Volatility</th>
                    </tr>
                  </thead>
                  <tbody>
                    {markowitz?.individual_stats && Object.entries(markowitz.individual_stats)
                      .sort(([, a], [, b]) => b.expected_return - a.expected_return)
                      .map(([ticker, stats]) => (
                        <tr key={ticker}>
                          <td className="font-medium text-text-primary">{ticker}</td>
                          <td className="text-right">
                            <span className={stats.expected_return >= 0 ? 'delta-chip-pos' : 'delta-chip-neg'}>
                              {pct(stats.expected_return)}
                            </span>
                          </td>
                          <td className="text-right text-text-secondary">{(stats.volatility ?? 0).toFixed(2)}%</td>
                        </tr>
                      ))}
                    {/* Portfolio rows */}
                    {markowitz && (
                      <>
                        <tr className="border-t-2 border-blue/30 bg-blue/5">
                          <td className="font-semibold text-blue-light">Max Sharpe</td>
                          <td className="text-right font-semibold text-blue-light">
                            {pct(markowitz.max_sharpe.expected_return)}
                          </td>
                          <td className="text-right text-blue-light">
                            {markowitz.max_sharpe.volatility.toFixed(2)}%
                          </td>
                        </tr>
                        <tr className="bg-emerald/5">
                          <td className="font-semibold text-emerald">Min Variance</td>
                          <td className="text-right font-semibold text-emerald">
                            {pct(markowitz.min_variance.expected_return)}
                          </td>
                          <td className="text-right text-emerald">
                            {markowitz.min_variance.volatility.toFixed(2)}%
                          </td>
                        </tr>
                      </>
                    )}
                    {riskParity && (
                      <tr className="bg-amber-500/5">
                        <td className="font-semibold text-amber-400">Risk Parity</td>
                        <td className="text-right font-semibold text-amber-400">
                          {pct(riskParity.expected_return)}
                        </td>
                        <td className="text-right text-amber-400">
                          {(riskParity.volatility ?? 0).toFixed(2)}%
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Risk Parity contributions */}
          {riskParity?.risk_contributions && strategy === 'risk_parity' && (
            <div className="card p-5">
              <h2 className="panel-title mb-4">Risk Contributions (Risk Parity)</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {Object.entries(riskParity.risk_contributions)
                  .sort(([, a], [, b]) => b - a)
                  .map(([ticker, rc]) => (
                    <div key={ticker} className="text-center p-3 bg-surface-hover rounded-lg">
                      <p className="text-xs text-text-muted">{ticker}</p>
                      <p className="text-lg font-bold text-text-primary">{rc.toFixed(1)}%</p>
                      <p className="text-xs text-text-muted">risk contribution</p>
                    </div>
                  ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Current vs Optimal Comparison */}
      {comparison && !comparison.error && !loading && (
        <div className="card p-5 space-y-4">
          <h2 className="panel-title flex items-center gap-2">
            <ArrowRightLeft size={14} />
            Current Portfolio vs Optimal
          </h2>
          <div className="table-responsive overflow-x-auto">
            <table className="table-pro w-full text-sm">
              <thead>
                <tr>
                  <th>Ticker</th>
                  <th className="!text-right">Current %</th>
                  <th className="!text-right">Optimal %</th>
                  <th className="!text-right">Diff</th>
                  <th className="!text-right">Action</th>
                  <th className="!text-right">Trade Value</th>
                </tr>
              </thead>
              <tbody>
                {comparison.rebalance?.trades?.map(t => (
                  <tr key={t.ticker}>
                    <td className="font-medium text-text-primary">{t.ticker}</td>
                    <td className="text-right">{(t.current_weight ?? 0).toFixed(1)}%</td>
                    <td className="text-right">{(t.target_weight ?? 0).toFixed(1)}%</td>
                    <td className="text-right">
                      <span className={clsx(
                        t.diff_weight > 0 ? 'delta-chip-pos' : t.diff_weight < 0 ? 'delta-chip-neg' : 'text-text-muted',
                      )}>
                        {(t.diff_weight ?? 0) > 0 ? '+' : ''}{(t.diff_weight ?? 0).toFixed(1)}%
                      </span>
                    </td>
                    <td className="text-right">
                      <span className={clsx(
                        t.action === 'BUY' ? 'badge-green' :
                        t.action === 'SELL' ? 'badge-red' :
                        'badge-neutral',
                      )}>
                        {t.action}
                      </span>
                    </td>
                    <td className={clsx('text-right', t.trade_value > 0 ? 'text-emerald' : t.trade_value < 0 ? 'text-rose-light' : '')}>
                      {(t.trade_value ?? 0) > 0 ? '+' : ''}{(t.trade_value ?? 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {comparison.rebalance && (
            <div className="flex gap-4 text-xs text-text-muted pt-2 border-t border-border">
              <span>Turnover: {comparison.rebalance.turnover_pct.toFixed(1)}%</span>
              <span>Trades needed: {comparison.rebalance.trade_count}</span>
              <span>Portfolio value: {(comparison.total_value ?? 0).toLocaleString('en-GB', { style: 'currency', currency: 'GBP' })}</span>
            </div>
          )}
        </div>
      )}

      {/* AQR Enhanced Portfolio Optimization (EPO) */}
      <div className="card p-5">
        <div className="flex items-center gap-2 mb-2">
          <Zap size={16} className="text-amber" />
          <h2 className="panel-title">AQR Enhanced Portfolio Optimization</h2>
          <span className="text-xs text-text-muted ml-auto">
            Pedersen, Babu &amp; Levine (AQR 2020)
          </span>
        </div>
        <p className="text-xs text-text-muted mb-4">
          Markowitz with correlation-off-diagonal shrinkage — stabilises out-of-sample Sharpe by reducing concentration in noise eigenvectors.
          Solves: <span className="font-mono">Σ_EPO = D · ((1−w)·C + w·I) · D</span>. Recommended w ∈ [0.5, 0.75].
        </p>

        <div className="flex items-end gap-4 mb-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <label className="block text-xs text-text-muted mb-1">
              Shrinkage intensity: <span className="font-mono text-text-primary">{epoShrinkage.toFixed(2)}</span>
            </label>
            <input
              type="range"
              min="0" max="1" step="0.05"
              value={epoShrinkage}
              onChange={(e) => setEpoShrinkage(parseFloat(e.target.value))}
              className="w-full accent-amber"
            />
            <div className="flex justify-between text-[10px] text-text-muted mt-0.5">
              <span>0 = Markowitz</span>
              <span>0.5</span>
              <span>1 = ignore correlations</span>
            </div>
          </div>
          <button
            type="button"
            onClick={handleRunEPO}
            disabled={epoLoading}
            className="btn btn-primary text-sm gap-2 disabled:opacity-50"
          >
            {epoLoading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
            {epoLoading ? 'Solving…' : 'Run EPO'}
          </button>
        </div>

        {epoError && (
          <div className="rounded-lg border border-rose/30 bg-rose/5 p-3 text-sm text-rose-light flex items-center gap-2 mb-3">
            <AlertTriangle size={14} /> {epoError}
          </div>
        )}

        {epoResult && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { label: 'EPO max-Sharpe', port: epoResult.max_sharpe, accent: 'border-amber/30 bg-amber/5' },
              { label: 'Unshrunk (Markowitz)', port: epoResult.unshrunk_max_sharpe, accent: 'border-border bg-surface-hover' },
            ].map(({ label, port, accent }) => {
              const topWeights = Object.entries(port.weights)
                .filter(([, w]) => w > 0.005)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 8)
              return (
                <div key={label} className={`rounded-lg border p-4 ${accent}`}>
                  <p className="text-xs uppercase tracking-wide text-text-muted mb-2">{label}</p>
                  <div className="grid grid-cols-3 gap-2 text-xs mb-3">
                    <div>
                      <p className="text-text-muted">Sharpe</p>
                      <p className="font-mono font-bold text-text-primary text-base">{(port.sharpe_ratio ?? 0).toFixed(3)}</p>
                    </div>
                    <div>
                      <p className="text-text-muted">Vol</p>
                      <p className="font-mono font-bold text-text-primary text-base">{(port.volatility ?? 0).toFixed(2)}%</p>
                    </div>
                    <div>
                      <p className="text-text-muted">HHI</p>
                      <p className="font-mono font-bold text-text-primary text-base">{(port.hhi_concentration ?? 0).toFixed(3)}</p>
                    </div>
                  </div>
                  <div className="space-y-1">
                    {topWeights.map(([t, w]) => (
                      <div key={t} className="flex items-center gap-2 text-xs">
                        <span className="font-mono text-text-secondary w-16 truncate">{t}</span>
                        <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                          <div className="h-full bg-blue rounded-full" style={{ width: `${w * 100}%` }} />
                        </div>
                        <span className="font-mono text-text-primary w-12 text-right">{(w * 100).toFixed(1)}%</span>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {epoResult && (
          <p className="text-[11px] text-text-muted pt-3 mt-3 border-t border-border">
            Lower HHI = more diversified. EPO trades a fraction of in-sample optimality for stability —
            usually beats unshrunk MVO out-of-sample on real portfolios.
          </p>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <div key={i} className="skeleton h-24" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="skeleton h-80" />
            <div className="skeleton h-80" />
          </div>
          <p className="text-sm text-text-muted text-center">Running optimization... fetching historical data from Yahoo Finance</p>
        </div>
      )}
    </div>
  )
}
